import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { TimelineEntry } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { PlanColorDot } from '../../components/plans/PlanColorDot';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { EQUIPMENT_LABEL } from '../../lib/format';
import { formatClockRange, formatDayLong, formatDuration } from '../../lib/planDates';
import { scaleLabel, SLOT_LABEL } from '../../lib/plans';

function equipmentLabel(equipment: TimelineEntry['equipment']): string | null {
  if (!equipment) return null;
  const name = EQUIPMENT_LABEL[equipment.kind] ?? equipment.kind;
  const count = equipment.count > 1 ? ` ×${equipment.count}` : '';
  const temp = equipment.ovenTempF ? ` ${equipment.ovenTempF}°F` : '';
  const mode = equipment.ovenMode ? ` ${equipment.ovenMode}` : '';
  return `${name}${count}${temp}${mode}`;
}

/**
 * Print/kitchen view: the merged timeline plus each recipe's own steps, in a
 * page that stands on its own (no `AppShell` — same reasoning as `CookPage`:
 * this is a dedicated full-screen route, not a tab of the plan editor, so
 * `@media print` only has to deal with content this page owns, not the app
 * chrome). A standalone route was chosen over a print stylesheet bolted onto
 * `PlanDetailPage` because the two views need different content — the plan
 * editor never lists full recipe steps inline — not just different styling.
 */
export function PlanPrintPage() {
  const { id = '' } = useParams<{ id: string }>();

  const plan = useQuery({
    queryKey: queryKeys.plans.detail(id),
    queryFn: () => api.plans.get(id),
    enabled: id !== '',
  });
  const timeline = useQuery({
    queryKey: queryKeys.plans.timeline(id),
    queryFn: () => api.plans.timeline(id),
    enabled: id !== '' && (plan.data?.recipes.length ?? 0) > 0,
  });
  const recipeIds = plan.data?.recipes.map((r) => r.recipeId) ?? [];
  const recipesQuery = useQuery({
    queryKey: queryKeys.plans.printRecipes(id, recipeIds),
    queryFn: () => Promise.all(recipeIds.map((recipeId) => api.recipes.get(recipeId))),
    enabled: recipeIds.length > 0,
  });

  if (plan.isPending) {
    return (
      <main className="mx-auto max-w-3xl p-6 text-sm text-muted" aria-busy>
        Loading…
      </main>
    );
  }
  if (plan.isError || !plan.data) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <Alert tone="error">{messageForError(plan.error)}</Alert>
        <Link
          to={`/plan/${id}`}
          className="mt-3 inline-block text-sm text-brand underline print:hidden"
        >
          Back to the meal
        </Link>
      </main>
    );
  }

  const p = plan.data;
  const recipeDetailById = new Map((recipesQuery.data ?? []).map((r) => [r.id, r]));
  const titleFor = (recipeId: string) =>
    timeline.data?.legend.find((l) => l.recipeId === recipeId)?.title ?? 'This meal';

  return (
    <div className="print-plan mx-auto max-w-3xl bg-cream p-6 text-ink print:bg-white">
      {/* Scoped, not global CSS: this route owns its own print layout rather
          than editing the shared stylesheet. Text renders black regardless of
          the browser's "print background graphics" setting; the small colour
          dots print in colour only when that setting is on — either way the
          text label next to each dot still identifies the recipe. */}
      <style>{`
        @media print {
          .print-plan, .print-plan h1, .print-plan h2, .print-plan h3, .print-plan p, .print-plan li {
            color: #000;
          }
          .print-plan .avoid-break {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="mb-4 flex items-center justify-between print:hidden">
        <Link
          to={`/plan/${p.id}`}
          className="text-sm text-brand underline-offset-2 hover:underline"
        >
          ← Back to the meal
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="min-h-11 rounded-lg bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-dark"
        >
          Print
        </button>
      </div>

      <header className="avoid-break mb-4 border-b border-line pb-3">
        <h1 className="text-2xl">
          {SLOT_LABEL[p.slot]} · {formatDayLong(p.date)}
        </h1>
        <p className="text-sm">
          Serves {p.servings}
          {p.serveAt ? ` · serve at ${p.serveAt}` : ''}
        </p>
      </header>

      {timeline.data && timeline.data.legend.length > 0 ? (
        <section className="avoid-break mb-4">
          <h2 className="text-lg">Legend</h2>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {timeline.data.legend.map((item) => (
              <li key={item.recipeId} className="flex items-center gap-1.5">
                <PlanColorDot colorIndex={item.colorIndex} label={item.title} />
                {item.title}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {timeline.isError ? (
        <Alert tone="error" className="mb-4">
          {messageForError(timeline.error)}
        </Alert>
      ) : null}

      {timeline.data && timeline.data.entries.length > 0 ? (
        <section className="mb-6">
          <h2 className="text-lg">Cooking timeline</h2>
          {!timeline.data.feasible || timeline.data.conflicts.length > 0 ? (
            <ul className="mt-1 flex flex-col gap-1 text-sm">
              {timeline.data.conflicts.map((conflict, i) => (
                <li key={`${conflict.kind}-${i}`}>
                  {conflict.message}
                  {conflict.suggestion ? ` — ${conflict.suggestion}` : ''}
                </li>
              ))}
            </ul>
          ) : null}
          <ol className="mt-2 flex flex-col text-sm">
            {timeline.data.entries.map((entry) => {
              const equipment = equipmentLabel(entry.equipment);
              return (
                <li
                  key={`${entry.recipeId}-${entry.stepId}-${entry.start}`}
                  className="avoid-break border-b border-line py-1"
                >
                  <strong className="tabular-nums">
                    {formatClockRange(entry.start, entry.end)}
                  </strong>{' '}
                  — <strong>{titleFor(entry.recipeId)}</strong>: {entry.text}
                  {entry.durationMin > 0
                    ? ` (${formatDuration(entry.durationMin)}${entry.attended ? ', hands-on' : ''})`
                    : ''}
                  {equipment ? ` [${equipment}]` : ''}
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      <section>
        <h2 className="text-lg">Recipes</h2>
        {p.recipes.length === 0 ? (
          <p className="text-sm text-muted">Nothing on this meal.</p>
        ) : (
          p.recipes.map((r) => {
            const detail = recipeDetailById.get(r.recipeId);
            const steps = detail ? [...detail.steps].sort((a, b) => a.sort - b.sort) : [];
            return (
              <article key={r.recipeId} className="avoid-break mt-3">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <PlanColorDot colorIndex={r.colorIndex} label={r.title} />
                  {r.title}
                  <span className="text-xs font-normal text-muted">{scaleLabel(r.scale)}</span>
                </h3>
                {recipesQuery.isPending ? (
                  <p className="text-sm text-muted">Loading steps…</p>
                ) : steps.length === 0 ? (
                  <p className="text-sm text-muted">No steps recorded for this recipe.</p>
                ) : (
                  <ol className="mt-1 list-decimal pl-5 text-sm">
                    {steps.map((step) => (
                      <li key={step.id} className="avoid-break">
                        {step.text}
                        {step.durationMin ? ` (${formatDuration(step.durationMin)})` : ''}
                      </li>
                    ))}
                  </ol>
                )}
              </article>
            );
          })
        )}
      </section>
    </div>
  );
}
